import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import QRCode from 'react-qr-code';
import 'leaflet/dist/leaflet.css';
import {
  ShieldCheck, WarningCircle, ArrowCounterClockwise, UsersFour, DeviceMobile,
  Envelope, CheckCircle, House, NotePencil, PaperPlaneTilt, Phone, HourglassMedium,
  Copy, X, ShareNetwork, Car,
} from '@phosphor-icons/react';
import Layout from '../../components/Layout';
import DialogModal from '../../components/DialogModal';
import LockedFeature from '../../components/LockedFeature';
import { useAuth } from '../../context/AuthContext';
import securityService from '../../services/securityService';
import parentService from '../../services/parentService';
import { animateMarkerTo, setMarkerHeading, pushBreadcrumbPoint } from '../../utils/mapAnimation';
import './security.css';

const initials = (n) => (n || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
const fmtTime = (iso) => iso
  ? new Date(iso).toLocaleTimeString('en-GH', { hour: '2-digit', minute: '2-digit' })
  : '';
const timeAgo = (iso) => {
  if (!iso) return '';
  const d = Math.floor((Date.now() - new Date(iso)) / 1000);
  if (d < 60)   return `${d}s ago`;
  if (d < 3600) return `${Math.floor(d / 60)}m ago`;
  return `${Math.floor(d / 3600)}h ago`;
};

const STATUS_META = {
  pending:             { headline: 'Still at School',           sub: 'Your child has not yet been picked up or boarded their bus.', color: '#d97706', bg: '#fef3c7' },
  en_route_bus:        { headline: 'On the School Bus',         sub: 'Your child is currently on the school bus heading home.', color: '#2563eb', bg: '#eff6ff' },
  en_route_collector:  { headline: 'Being Collected',           sub: 'Your child has been released to the pickup person. Live location is active.', color: '#16a34a', bg: '#f0fdf4' },
  arrived_unconfirmed: { headline: 'Arrived — Please Confirm',  sub: 'Your child has been marked as arrived. Please confirm they are safely home.', color: '#7c3aed', bg: '#f5f3ff' },
  safe_confirmed:      { headline: 'Home Safe',                 sub: 'You have confirmed your child is safely home. Thank you!', color: '#16a34a', bg: '#f0fdf4', headlineIcon: true },
};

export default function ParentSecurityPortalPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [children, setChildren] = useState([]);
  const [selectedId, setSelectedId] = useState(searchParams.get('student') || '');
  const [status, setStatus]         = useState(null);   // security status for selected child
  const [busPos, setBusPos]         = useState(null);   // { lat, lng, speed_kmh }
  const [collectorPos, setCollectorPos] = useState(null);
  const [noteText, setNoteText]     = useState('');
  const [submittingNote, setSubmittingNote] = useState(false);
  const [noteSuccess, setNoteSuccess]   = useState(false);
  const [confirming, setConfirming]     = useState(false);
  const [qrToken, setQrToken]               = useState(null);
  const [loadingQr, setLoadingQr]           = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareEmail, setShareEmail]         = useState('');
  const [sendingShareEmail, setSendingShareEmail] = useState(false);
  const [shareEmailSent, setShareEmailSent] = useState(false);
  const [shareError, setShareError]         = useState(null);
  const [copyFeedback, setCopyFeedback]     = useState(false);
  const [loadingChildren, setLoadingChildren] = useState(true);
  const [loadingStatus, setLoadingStatus]     = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [alertDialog, setAlertDialog] = useState(null); // { title, message, type }
  const [onMyWayEta, setOnMyWayEta] = useState('');
  const [sendingOnMyWay, setSendingOnMyWay] = useState(false);
  const [cancellingOnMyWay, setCancellingOnMyWay] = useState(false);
  const [pickupPersons, setPickupPersons] = useState([]);
  const [showPickupPersonForm, setShowPickupPersonForm] = useState(false);
  const [pickupPersonForm, setPickupPersonForm] = useState({ name: '', phone: '', relationship: '', notes: '' });
  const [savingPickupPerson, setSavingPickupPerson] = useState(false);

  const miniMapRef         = useRef(null);
  const miniMapInstanceRef = useRef(null);
  const leafletRef         = useRef(null);
  const busMarkerRef       = useRef(null);
  const collectorMarkerRef = useRef(null);
  const busTrailRef        = useRef(null);
  const busPointsRef       = useRef([]);
  const collectorTrailRef  = useRef(null);
  const collectorPointsRef = useRef([]);

  // Lazily creates the bus pin + breadcrumb on its first real position
  // (never at the child's home coordinate, which was the placeholder here
  // before — misleading, since it looked like the bus had already arrived).
  const upsertBusPin = useCallback((lat, lng, heading) => {
    const L = leafletRef.current, map = miniMapInstanceRef.current;
    if (!L || !map || lat == null || lng == null) return;
    if (!busMarkerRef.current) {
      const busIcon = L.divIcon({
        html: `<div style="position:relative;width:26px;height:26px;">
          <div style="font-size:22px;line-height:1;text-align:center;">🚌</div>
          <div class="map-heading-rotor" style="position:absolute;bottom:-3px;left:50%;margin-left:-5px;width:10px;height:10px;">
            <svg viewBox="0 0 12 12" width="10" height="10"><polygon points="6,0 11,11 6,8 1,11" fill="#2563eb" stroke="white" stroke-width="0.5"/></svg>
          </div>
        </div>`,
        className: '', iconSize: [26, 26], iconAnchor: [13, 13],
      });
      busMarkerRef.current = L.marker([lat, lng], { icon: busIcon }).addTo(map).bindTooltip('School Bus', { direction: 'top' });
      busPointsRef.current = [[lat, lng]];
      busTrailRef.current = L.polyline([[lat, lng]], { color: '#2563eb', weight: 2, opacity: 0.4, dashArray: '4 6' }).addTo(map);
      map.setView([lat, lng], 14);
    } else {
      animateMarkerTo(busMarkerRef.current, [lat, lng]);
      setMarkerHeading(busMarkerRef.current, heading);
      busPointsRef.current = pushBreadcrumbPoint(busPointsRef.current, [lat, lng]);
      busTrailRef.current?.setLatLngs(busPointsRef.current);
      map.panTo([lat, lng], { animate: true });
    }
  }, []);

  // Same for the on-foot collector pin — no heading available for that model.
  const upsertCollectorPin = useCallback((lat, lng) => {
    const L = leafletRef.current, map = miniMapInstanceRef.current;
    if (!L || !map || lat == null || lng == null) return;
    if (!collectorMarkerRef.current) {
      const colIcon = L.divIcon({ html: '<div style="font-size:22px">📍</div>', className: '', iconSize: [22, 22], iconAnchor: [11, 22] });
      collectorMarkerRef.current = L.marker([lat, lng], { icon: colIcon }).addTo(map).bindTooltip('Collector', { direction: 'top' });
      collectorPointsRef.current = [[lat, lng]];
      collectorTrailRef.current = L.polyline([[lat, lng]], { color: '#16a34a', weight: 2, opacity: 0.4, dashArray: '4 6' }).addTo(map);
      map.setView([lat, lng], 14);
    } else {
      animateMarkerTo(collectorMarkerRef.current, [lat, lng]);
      collectorPointsRef.current = pushBreadcrumbPoint(collectorPointsRef.current, [lat, lng]);
      collectorTrailRef.current?.setLatLngs(collectorPointsRef.current);
      map.panTo([lat, lng], { animate: true });
    }
  }, []);

  // Load parent's own children
  useEffect(() => {
    parentService.getChildren()
      .then(data => {
        const list = Array.isArray(data) ? data : [];
        setChildren(list);
        if (!selectedId && list.length > 0) setSelectedId(String(list[0].id));
      })
      .catch(() => setLoadError('Could not load your children.'))
      .finally(() => setLoadingChildren(false));
  }, [selectedId]);

  // Load security status for selected child
  const loadStatus = useCallback(() => {
    if (!selectedId) return;
    setLoadingStatus(true);
    securityService.getStudentStatus(selectedId)
      .then(data => { setStatus(data); setLoadError(null); })
      .catch(err => {
        if (err.status === 404 || err.message?.includes('404')) {
          setStatus({ arrival_status: 'pending', notes: [], pickup_method: 'parent' });
        } else {
          setLoadError('Could not load security status.');
        }
      })
      .finally(() => setLoadingStatus(false));
  }, [selectedId]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  // Load authorized pickup persons for the selected child
  const loadPickupPersons = useCallback(() => {
    if (!selectedId) return;
    securityService.listPickupPersons(selectedId)
      .then(data => setPickupPersons(Array.isArray(data) ? data : []))
      .catch(() => setPickupPersons([]));
  }, [selectedId]);

  useEffect(() => { loadPickupPersons(); }, [loadPickupPersons]);

  const submitPickupPerson = async (e) => {
    e.preventDefault();
    if (!pickupPersonForm.name.trim()) return;
    setSavingPickupPerson(true);
    try {
      await securityService.addPickupPerson(selectedId, pickupPersonForm);
      setPickupPersonForm({ name: '', phone: '', relationship: '', notes: '' });
      setShowPickupPersonForm(false);
      loadPickupPersons();
    } catch (err) {
      setAlertDialog({ title: 'Could Not Add', message: err.response?.data?.detail || err.message || 'Failed to add pickup person.', type: 'error' });
    } finally {
      setSavingPickupPerson(false);
    }
  };

  const togglePickupPersonActive = async (person) => {
    try {
      await securityService.updatePickupPerson(person.id, { is_active: !person.is_active });
      loadPickupPersons();
    } catch (err) {
      setAlertDialog({ title: 'Could Not Update', message: err.response?.data?.detail || err.message, type: 'error' });
    }
  };

  const removePickupPerson = async (person) => {
    try {
      await securityService.deletePickupPerson(person.id);
      loadPickupPersons();
    } catch (err) {
      setAlertDialog({ title: 'Could Not Remove', message: err.response?.data?.detail || err.message, type: 'error' });
    }
  };

  // Fetch today's QR token for the selected child
  useEffect(() => {
    if (!selectedId) return;
    setLoadingQr(true);
    securityService.getStudentQr(selectedId)
      .then(data => setQrToken(data.token))
      .catch(() => setQrToken(null))
      .finally(() => setLoadingQr(false));
  }, [selectedId]);

  // Poll status every 30s when child is in transit
  useEffect(() => {
    if (!status) return;
    const active = ['en_route_bus', 'en_route_collector', 'arrived_unconfirmed'].includes(status.arrival_status);
    if (!active) return;
    const t = setInterval(loadStatus, 30000);
    return () => clearInterval(t);
  }, [status?.arrival_status, loadStatus]);

  // Bus location: 30s reconciliation poll (primary updates arrive via SSE push below)
  useEffect(() => {
    if (!status?.transport_route_id || status?.arrival_status !== 'en_route_bus') {
      setBusPos(null);
      return;
    }
    const fetchBus = () => {
      securityService.getTransportLocation(status.transport_route_id)
        .then(data => {
          setBusPos(data);
          upsertBusPin(data.lat, data.lng, data.heading);
        })
        .catch(() => {});
    };
    fetchBus();
    const t = setInterval(fetchBus, 30000);
    return () => clearInterval(t);
  }, [status?.transport_route_id, status?.arrival_status, upsertBusPin]);

  // Live push: bus_location events over SSE update the mini-map instantly
  useEffect(() => {
    if (!user?.school_id || status?.arrival_status !== 'en_route_bus' || !status?.transport_route_id) return;
    let es;
    try {
      es = new EventSource(securityService.getEventsStreamUrl(user.school_id));
    } catch (err) {
      console.warn('SSE not available:', err);
      return;
    }

    es.onmessage = (ev) => {
      try {
        const data = JSON.parse(ev.data);
        if (data.type === 'bus_location' && data.route_id === status.transport_route_id) {
          setBusPos(data);
          upsertBusPin(data.lat, data.lng, data.heading);
        }
      } catch (err) {
        console.warn('Failed parsing SSE event', err);
      }
    };
    es.onerror = (err) => console.warn('SSE error', err);

    return () => { try { es.close(); } catch (_) {} };
  }, [user?.school_id, status?.arrival_status, status?.transport_route_id, upsertBusPin]);

  // Poll collector location when child is being collected
  useEffect(() => {
    if (!status?.collector_session_token || status?.arrival_status !== 'en_route_collector') {
      setCollectorPos(null);
      return;
    }
    const token = status.collector_session_token;
    const fetchCollector = () => {
      securityService.getCollectorLiveLocation(token)
        .then(data => {
          if (data.lat !== null) {
            setCollectorPos(data);
            upsertCollectorPin(data.lat, data.lng);
          }
        })
        .catch(() => {});
    };
    fetchCollector();
    const t = setInterval(fetchCollector, 10000);
    return () => clearInterval(t);
  }, [status?.collector_session_token, status?.arrival_status, upsertCollectorPin]);

  // Mini map for en_route states. The bus/collector pin itself is added
  // lazily by upsertBusPin/upsertCollectorPin on the first real position —
  // this effect just sets up the map + home pin and waits.
  useEffect(() => {
    const isEnRoute = ['en_route_bus', 'en_route_collector'].includes(status?.arrival_status);
    const resetRefs = () => {
      busMarkerRef.current = null;
      collectorMarkerRef.current = null;
      busTrailRef.current = null;
      busPointsRef.current = [];
      collectorTrailRef.current = null;
      collectorPointsRef.current = [];
    };
    if (!isEnRoute || !miniMapRef.current) {
      if (miniMapInstanceRef.current) {
        miniMapInstanceRef.current.remove();
        miniMapInstanceRef.current = null;
        resetRefs();
      }
      return;
    }
    if (miniMapInstanceRef.current) return; // already initialized for this state

    let isMounted = true;
    import('leaflet').then(L => {
      if (!isMounted || !miniMapRef.current || miniMapInstanceRef.current) return;
      const defaultLat = status?.home_lat || 5.556;
      const defaultLng = status?.home_lng || -0.187;
      const map = L.map(miniMapRef.current, { zoomControl: false, attributionControl: false })
        .setView([defaultLat, defaultLng], 13);
      L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', { maxZoom: 19 }).addTo(map);

      // Home pin
      if (status?.home_lat && status?.home_lng) {
        const homeIcon = L.divIcon({ html: '<div style="font-size:20px">🏠</div>', className: '', iconSize: [20, 20], iconAnchor: [10, 20] });
        L.marker([status.home_lat, status.home_lng], { icon: homeIcon }).addTo(map)
          .bindTooltip('Home', { permanent: true, direction: 'top', offset: [0, -20] });
      }

      leafletRef.current = L;
      miniMapInstanceRef.current = map;
      // A poll/SSE payload may already be in hand by the time the map
      // finishes loading — place the pin immediately instead of waiting
      // for the next update.
      if (status.arrival_status === 'en_route_bus' && busPos) upsertBusPin(busPos.lat, busPos.lng, busPos.heading);
      if (status.arrival_status === 'en_route_collector' && collectorPos) upsertCollectorPin(collectorPos.lat, collectorPos.lng);
    });

    return () => {
      isMounted = false;
      if (miniMapInstanceRef.current) {
        miniMapInstanceRef.current.remove();
        miniMapInstanceRef.current = null;
        resetRefs();
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status?.arrival_status, selectedId]);

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      await securityService.confirmStudentArrival(selectedId);
      await loadStatus();
    } catch (err) {
      setAlertDialog({ title: 'Confirm Failed', message: `Confirm failed: ${err.message}`, type: 'error' });
    } finally {
      setConfirming(false);
    }
  };

  const handleSendOnMyWay = async () => {
    setSendingOnMyWay(true);
    try {
      const eta = onMyWayEta ? parseInt(onMyWayEta, 10) : undefined;
      await securityService.sendOnMyWay(selectedId, eta);
      await loadStatus();
    } catch (err) {
      setAlertDialog({ title: 'Could Not Send Notice', message: err.message || 'Failed to send on-my-way notice.', type: 'error' });
    } finally {
      setSendingOnMyWay(false);
    }
  };

  const handleCancelOnMyWay = async () => {
    setCancellingOnMyWay(true);
    try {
      await securityService.cancelOnMyWay(selectedId);
      setOnMyWayEta('');
      await loadStatus();
    } catch (err) {
      setAlertDialog({ title: 'Could Not Cancel', message: err.message || 'Failed to cancel on-my-way notice.', type: 'error' });
    } finally {
      setCancellingOnMyWay(false);
    }
  };

  const handleSubmitNote = async () => {
    if (!noteText.trim()) return;
    setSubmittingNote(true);
    try {
      await securityService.addParentNote(selectedId, noteText.trim());
      setNoteText('');
      setNoteSuccess(true);
      setTimeout(() => setNoteSuccess(false), 3000);
      await loadStatus();
    } catch (err) {
      setAlertDialog({ title: 'Note Failed', message: `Failed to send note: ${err.message}`, type: 'error' });
    } finally {
      setSubmittingNote(false);
    }
  };

  const openShareModal = () => {
    setShareEmail('');
    setShareEmailSent(false);
    setShareError(null);
    setCopyFeedback(false);
    setShowShareModal(true);
  };

  const closeShareModal = () => setShowShareModal(false);

  const handleSendShareEmail = async () => {
    if (!selectedId || !shareEmail.trim()) return;
    setSendingShareEmail(true);
    setShareError(null);
    try {
      await securityService.emailStudentQr(selectedId, shareEmail.trim());
      setShareEmailSent(true);
    } catch (err) {
      setShareError(err.message || 'Failed to send QR email.');
    } finally {
      setSendingShareEmail(false);
    }
  };

  const handleCopyQrCode = async () => {
    if (!qrToken) return;
    try {
      await navigator.clipboard.writeText(qrToken);
      setCopyFeedback(true);
      setTimeout(() => setCopyFeedback(false), 2000);
    } catch (err) {
      setShareError('Could not copy — select and copy the code manually.');
    }
  };

  const selectedChild = children.find(c => String(c.id) === String(selectedId));
  const meta    = STATUS_META[status?.arrival_status] || STATUS_META.pending;
  const livePos = status?.arrival_status === 'en_route_bus' ? busPos : collectorPos;

  return (
    <Layout>
    <div className="parent-portal-page">
      {/* Header */}
      <div className="parent-portal-header">
        <div className="parent-portal-header-left">
          <div style={{ fontSize: 26 }}><ShieldCheck size={26} /></div>
          <div>
            <h1>Child Safety</h1>
            <p>Your child's real-time status</p>
          </div>
        </div>
        <button onClick={loadStatus} style={{
          background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)',
          borderRadius: 8, color: 'white', padding: '6px 12px', fontSize: 12, cursor: 'pointer',
        }}><ArrowCounterClockwise size={14} /> Refresh</button>
      </div>

      {/* Error */}
      {loadError && (
        <div style={{ margin: '12px 16px', padding: 12, background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 10, fontSize: 13, color: '#dc2626', display: 'flex', alignItems: 'center', gap: 6 }}>
          <WarningCircle size={16} /> {loadError}
        </div>
      )}

      {/* Loading children */}
      {loadingChildren && (
        <div style={{ padding: 40, textAlign: 'center', color: '#6b7280', fontSize: 13 }}>Loading...</div>
      )}

      {/* No children linked */}
      {!loadingChildren && children.length === 0 && !loadError && (
        <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}><UsersFour size={40} /></div>
          <p>No children are linked to your account yet.</p>
          <p style={{ fontSize: 12 }}>Contact the school office to link your children.</p>
        </div>
      )}

      {/* Child switcher — only shows THIS parent's children */}
      {children.length > 1 && (
        <div className="parent-switcher">
          <span style={{ fontSize: 11, color: '#6b7280', flexShrink: 0 }}>Viewing:</span>
          {children.map(c => (
            <button
              key={c.id}
              onClick={() => {
                setSelectedId(String(c.id));
                setStatus(null);
                setBusPos(null);
                setCollectorPos(null);
                navigate(`/security/parent-confirm?student=${c.id}`, { replace: true });
              }}
              style={{
                padding: '5px 12px', borderRadius: 20, border: '1px solid #e5e7eb', fontSize: 12,
                fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
                background: String(selectedId) === String(c.id) ? '#16a34a' : 'white',
                color: String(selectedId) === String(c.id) ? 'white' : '#6b7280',
              }}
            >
              {(c.name || '').split(' ')[0]}
            </button>
          ))}
        </div>
      )}

      {selectedChild && (
        <div className="parent-portal-body">
          {/* Student card */}
          <div className="parent-student-card">
            <div className="parent-student-avatar-lg">{initials(selectedChild.name)}</div>
            <div style={{ flex: 1 }}>
              <p className="parent-student-name">{selectedChild.name}</p>
              <p className="parent-student-meta">
                {selectedChild.class_name || 'Class not set'}
                {status?.pickup_method && ` · ${status.pickup_method === 'transport' ? 'School Bus' : 'Parent Pickup'}`}
              </p>
            </div>
            {status?.ghana_post_address && (
              <div className="parent-gps-badge">
                <div className="parent-gps-code">{status.ghana_post_address}</div>
                <div className="parent-gps-label">Ghana Post GPS</div>
                {status.neighbourhood && (
                  <div style={{ fontSize: 10, color: '#94a3b8', textAlign: 'center', marginTop: 2 }}>
                    {status.neighbourhood}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Today's pickup QR code */}
          <div style={{
            background: 'white', borderRadius: 16, border: '1px solid #e5e7eb',
            padding: '20px 24px', marginBottom: 16,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#111827', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <DeviceMobile size={16} /> Today's Pickup QR
                </div>
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                  Show this to the security officer at pickup. Resets each school day.
                </div>
              </div>
              <LockedFeature module="pickup_advanced">
                <button
                  onClick={openShareModal}
                  disabled={!qrToken}
                  style={{
                    fontSize: 12, padding: '5px 12px', borderRadius: 8,
                    border: '1px solid #d1d5db', background: 'white',
                    color: '#374151', cursor: 'pointer', whiteSpace: 'nowrap',
                    opacity: !qrToken ? 0.5 : 1,
                  }}
                >
                  <ShareNetwork size={14} /> Share with Pickup Person
                </button>
              </LockedFeature>
            </div>
            {loadingQr && (
              <div style={{ textAlign: 'center', padding: 20, color: '#9ca3af', fontSize: 13 }}>
                Loading QR…
              </div>
            )}
            {!loadingQr && qrToken && (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '8px 0' }}>
                <div style={{ padding: 12, background: 'white', borderRadius: 10, border: '1px solid #e5e7eb', display: 'inline-block' }}>
                  <QRCode value={qrToken} size={180} />
                </div>
              </div>
            )}
            {!loadingQr && !qrToken && (
              <div style={{ textAlign: 'center', padding: 20, color: '#9ca3af', fontSize: 13 }}>
                QR not available. Contact the school.
              </div>
            )}
          </div>

          {/* Release hold banner — boolean only, reason stays staff-only */}
          {status?.release_hold && (
            <div style={{
              background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 16,
              padding: '16px 20px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10,
            }}>
              <WarningCircle size={20} weight="fill" style={{ color: '#dc2626', flexShrink: 0 }} />
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#dc2626' }}>Release Hold Active</div>
                <div style={{ fontSize: 12, color: '#b91c1c' }}>
                  This student currently cannot be released. Contact the school office for details.
                </div>
              </div>
            </div>
          )}

          {/* Authorized pickup persons — parent self-service */}
          <div style={{
            background: 'white', borderRadius: 16, border: '1px solid #e5e7eb',
            padding: '20px 24px', marginBottom: 16,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#111827', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <UsersFour size={16} /> Authorized Pickup Persons
                </div>
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                  Anyone else who may collect {selectedChild?.name?.split(' ')[0] || 'your child'}. Staff check ID against this list at the gate.
                </div>
              </div>
              <LockedFeature module="student_safety">
                <button
                  onClick={() => setShowPickupPersonForm(v => !v)}
                  style={{ fontSize: 12, padding: '5px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: 'white', color: '#374151', cursor: 'pointer', whiteSpace: 'nowrap' }}
                >
                  {showPickupPersonForm ? 'Cancel' : '+ Add'}
                </button>
              </LockedFeature>
            </div>

            {showPickupPersonForm && (
              <form onSubmit={submitPickupPerson} style={{ display: 'grid', gap: 8, marginBottom: 14, padding: 12, background: '#f9fafb', borderRadius: 10 }}>
                <input
                  placeholder="Full name" required value={pickupPersonForm.name}
                  onChange={e => setPickupPersonForm({ ...pickupPersonForm, name: e.target.value })}
                  style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13 }}
                />
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    placeholder="Phone" value={pickupPersonForm.phone}
                    onChange={e => setPickupPersonForm({ ...pickupPersonForm, phone: e.target.value })}
                    style={{ flex: 1, padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13 }}
                  />
                  <input
                    placeholder="Relationship (e.g. Uncle)" value={pickupPersonForm.relationship}
                    onChange={e => setPickupPersonForm({ ...pickupPersonForm, relationship: e.target.value })}
                    style={{ flex: 1, padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13 }}
                  />
                </div>
                <input
                  placeholder="Notes (optional, e.g. drives a red Corolla)" value={pickupPersonForm.notes}
                  onChange={e => setPickupPersonForm({ ...pickupPersonForm, notes: e.target.value })}
                  style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13 }}
                />
                <button type="submit" disabled={savingPickupPerson} style={{
                  background: '#16a34a', color: 'white', border: 'none', borderRadius: 8,
                  padding: '8px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer',
                }}>
                  {savingPickupPerson ? 'Saving...' : 'Add Person'}
                </button>
              </form>
            )}

            {pickupPersons.length === 0 ? (
              <p style={{ fontSize: 12, color: '#9ca3af', margin: 0 }}>No additional pickup persons added yet.</p>
            ) : (
              <div style={{ display: 'grid', gap: 8 }}>
                {pickupPersons.map(person => (
                  <div key={person.id} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '8px 12px', borderRadius: 8, border: '1px solid #e5e7eb',
                    opacity: person.is_active ? 1 : 0.5,
                  }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>
                        {person.name} {!person.is_active && <span style={{ fontSize: 11, color: '#9ca3af' }}>(inactive)</span>}
                      </div>
                      <div style={{ fontSize: 11, color: '#6b7280' }}>
                        {[person.relationship, person.phone].filter(Boolean).join(' · ')}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <LockedFeature module="student_safety">
                        <button onClick={() => togglePickupPersonActive(person)} style={{ fontSize: 11, background: 'none', border: '1px solid #d1d5db', borderRadius: 6, padding: '3px 8px', cursor: 'pointer', color: '#6b7280' }}>
                          {person.is_active ? 'Deactivate' : 'Reactivate'}
                        </button>
                      </LockedFeature>
                      <LockedFeature module="student_safety">
                        <button onClick={() => removePickupPerson(person)} style={{ fontSize: 11, background: 'none', border: '1px solid #fca5a5', borderRadius: 6, padding: '3px 8px', cursor: 'pointer', color: '#dc2626' }}>
                          Remove
                        </button>
                      </LockedFeature>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Status card */}
          {loadingStatus && !status && (
            <div style={{ padding: 24, textAlign: 'center', color: '#6b7280', fontSize: 13 }}>
              Loading status...
            </div>
          )}

          {status && (
            <div className={`parent-status-card ${status.arrival_status || 'pending'}`}
              style={{ borderLeft: `4px solid ${meta.color}`, background: meta.bg }}>
              <p className="parent-status-title" style={{ color: meta.color }}>
                {meta.headline}{meta.headlineIcon && <> <CheckCircle size={16} weight="fill" /></>}
              </p>
              <p className="parent-status-sub">{meta.sub}</p>
              {status.arrival_time && (
                <p className="parent-status-time">
                  {status.arrival_status === 'safe_confirmed'
                    ? `Arrived ${fmtTime(status.arrival_time)} · Confirmed ${fmtTime(status.confirmed_at)}`
                    : `Since ${fmtTime(status.arrival_time)}`}
                </p>
              )}

              {/* Live map for transit states */}
              {['en_route_bus', 'en_route_collector'].includes(status.arrival_status) && (
                <div style={{ marginTop: 12 }}>
                  <div id="parent-mini-map" ref={miniMapRef} style={{ height: 200, borderRadius: 12, overflow: 'hidden' }} />
                  {livePos && (
                    <div style={{
                      display: 'flex', gap: 16, justifyContent: 'center', marginTop: 8,
                      fontSize: 11, fontFamily: 'monospace', color: meta.color,
                    }}>
                      <span>LAT {livePos.lat?.toFixed(5)}</span>
                      <span>LNG {livePos.lng?.toFixed(5)}</span>
                      {livePos.speed_kmh && <span>{livePos.speed_kmh} km/h</span>}
                      <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#16a34a', animation: 'pulse-dot 1s infinite' }} />
                        LIVE
                      </span>
                    </div>
                  )}
                  {!livePos && (
                    <div style={{ fontSize: 12, color: '#6b7280', textAlign: 'center', marginTop: 8 }}>
                      Waiting for live location...
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* "On My Way" — advance notice before physically arriving at the gate */}
          {status?.arrival_status === 'pending' && !status?.parent_on_the_way && (
            <div style={{
              background: 'white', borderRadius: 16, border: '1px solid #e5e7eb',
              padding: '20px 24px', marginBottom: 16,
            }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#111827', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                <Car size={18} weight="fill" /> Heading to School?
              </div>
              <p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 14px' }}>
                Let the school know you're on your way so they can have {selectedChild?.name?.split(' ')[0] || 'your child'} ready to go.
              </p>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  type="number"
                  min="1"
                  placeholder="ETA (min)"
                  value={onMyWayEta}
                  onChange={e => setOnMyWayEta(e.target.value)}
                  style={{
                    width: 100, padding: '10px 12px', borderRadius: 8,
                    border: '1px solid #d1d5db', fontSize: 13, outline: 'none',
                  }}
                />
                <LockedFeature module="student_safety">
                  <button
                    onClick={handleSendOnMyWay}
                    disabled={sendingOnMyWay}
                    style={{
                      flex: 1, background: '#16a34a', color: 'white', border: 'none',
                      borderRadius: 8, padding: '10px 16px', fontSize: 13, fontWeight: 700,
                      cursor: sendingOnMyWay ? 'default' : 'pointer', opacity: sendingOnMyWay ? 0.7 : 1,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    }}
                  >
                    {sendingOnMyWay ? <><HourglassMedium size={14} /> Sending...</> : <><Car size={14} weight="fill" /> I'm On My Way</>}
                  </button>
                </LockedFeature>
              </div>
            </div>
          )}

          {status?.arrival_status === 'pending' && status?.parent_on_the_way && (
            <div style={{
              background: '#eff6ff', borderRadius: 16, border: '1px solid #bfdbfe',
              padding: '20px 24px', marginBottom: 16,
            }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#2563eb', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                <Car size={18} weight="fill" /> The school has been notified
              </div>
              <p style={{ fontSize: 12, color: '#3b82f6', margin: '0 0 12px' }}>
                {status.parent_eta_minutes
                  ? `You said you'd arrive in about ${status.parent_eta_minutes} minutes.`
                  : "They know you're on your way."}
                {status.parent_on_the_way_at && ` Sent ${timeAgo(status.parent_on_the_way_at)}.`}
              </p>
              <LockedFeature module="student_safety">
                <button
                  onClick={handleCancelOnMyWay}
                  disabled={cancellingOnMyWay}
                  style={{
                    background: 'white', color: '#2563eb', border: '1px solid #bfdbfe',
                    borderRadius: 8, padding: '8px 14px', fontSize: 12, fontWeight: 600,
                    cursor: cancellingOnMyWay ? 'default' : 'pointer',
                  }}
                >
                  {cancellingOnMyWay ? 'Cancelling...' : 'Cancel Notice'}
                </button>
              </LockedFeature>
            </div>
          )}

          {/* Confirm arrival button */}
          {status?.arrival_status === 'arrived_unconfirmed' && (
            <button
              className="parent-confirm-btn"
              onClick={handleConfirm}
              disabled={confirming}
            >
              {confirming
                ? <><HourglassMedium size={16} /> Confirming...</>
                : <><CheckCircle size={16} weight="fill" /> My Child is Home Safely</>}
            </button>
          )}

          {/* Confirmed banner */}
          {status?.arrival_status === 'safe_confirmed' && status.confirmed_at && (
            <div className="parent-confirmed-banner">
              <span className="icon"><House size={24} weight="fill" /></span>
              <h3>Thank you for confirming!</h3>
              <p>Confirmed at {fmtTime(status.confirmed_at)} · The school has been notified.</p>
            </div>
          )}

          {/* Notes history */}
          {status?.notes?.length > 0 && (
            <div className="parent-notes-section">
              <div className="parent-notes-header">
                <p className="parent-notes-title"><NotePencil size={16} /> Notes &amp; Messages</p>
                <span className="parent-notes-count">{status.notes.length}</span>
              </div>
              {[...status.notes].map(note => (
                <div key={note.id} className="parent-note-item">
                  <p className="parent-note-text">{note.text}</p>
                  <div className="parent-note-meta">
                    <span>{timeAgo(note.created_at)}</span>
                    {note.is_confirmation && (
                      <span className="parent-note-confirmation-badge"><CheckCircle size={12} weight="fill" /> Safe Arrival</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Add note */}
          <div className="parent-add-note">
            <p className="parent-add-note-title"><PaperPlaneTilt size={16} /> Leave a Note for the School</p>
            <p style={{ fontSize: 12, color: '#94a3b8', margin: '0 0 10px' }}>
              Visible to security staff and teachers. Use for anything important.
            </p>
            <textarea
              className="parent-note-textarea"
              rows={3}
              placeholder="e.g. Dad is picking up today. He drives a black Honda CRV..."
              value={noteText}
              onChange={e => setNoteText(e.target.value)}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 }}>
              <button
                className="parent-note-submit-btn"
                onClick={handleSubmitNote}
                disabled={!noteText.trim() || submittingNote}
              >
                {submittingNote ? 'Sending...' : 'Send Note'}
              </button>
              {noteSuccess && (
                <span style={{ fontSize: 12, color: '#16a34a', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <CheckCircle size={14} weight="fill" /> Note sent
                </span>
              )}
            </div>
          </div>

          {/* Contact school */}
          <div style={{
            background: 'white', borderRadius: 16, padding: '16px 20px',
            border: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', marginBottom: 2 }}>
                Need to speak to someone?
              </div>
              <div style={{ fontSize: 12, color: '#6b7280' }}>Call the school office directly</div>
            </div>
            <a href="tel:" style={{
              background: '#16a34a', color: 'white', borderRadius: 10,
              padding: '8px 16px', fontSize: 13, fontWeight: 700, textDecoration: 'none',
            }}>
              <Phone size={14} weight="fill" /> Call Now
            </a>
          </div>
        </div>
      )}

      {/* Share QR modal — email to a pickup person, or copy the code to paste anywhere (WhatsApp, SMS, etc.) */}
      {showShareModal && (
        <div className="sec-modal-overlay" onClick={closeShareModal}>
          <div className="sec-modal" onClick={e => e.stopPropagation()}>
            <p className="sec-modal-title">Share Pickup QR{selectedChild ? ` — ${selectedChild.name}` : ''}</p>
            <p style={{ fontSize: 12, color: '#6b7280', margin: '-12px 0 16px' }}>
              Send this code to whoever is collecting your child. They can show the email
              at the gate, or you can copy the code and paste it into WhatsApp, SMS, or anywhere else.
            </p>

            {qrToken && (
              <div style={{
                background: 'white', border: '1px solid #e5e7eb', borderRadius: 12,
                padding: 16, textAlign: 'center', marginBottom: 20,
              }}>
                <QRCode value={qrToken} size={140} level="H" />
              </div>
            )}

            <label style={{ fontSize: 12, color: '#6b7280', display: 'block', marginBottom: 6 }}>
              Pickup person's email address
            </label>
            <input
              type="email"
              value={shareEmail}
              onChange={e => { setShareEmail(e.target.value); setShareEmailSent(false); setShareError(null); }}
              placeholder="e.g. auntie.ama@example.com"
              autoFocus
              style={{
                width: '100%', boxSizing: 'border-box', padding: '10px 12px',
                borderRadius: 8, border: '1px solid #d1fae5', fontSize: 13,
                outline: 'none', background: '#f8fafc', color: '#111827', marginBottom: 10,
              }}
            />

            {shareError && (
              <div style={{ fontSize: 12, color: '#dc2626', marginBottom: 10 }}>{shareError}</div>
            )}
            {shareEmailSent && (
              <div style={{ fontSize: 12, color: '#16a34a', fontWeight: 600, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 4 }}>
                <CheckCircle size={14} weight="fill" /> Sent to {shareEmail.trim()}
              </div>
            )}

            <button
              className="sec-modal-btn primary"
              style={{ width: '100%', marginBottom: 16 }}
              onClick={handleSendShareEmail}
              disabled={!shareEmail.trim() || sendingShareEmail}
            >
              {sendingShareEmail
                ? <><HourglassMedium size={14} /> Sending...</>
                : <><Envelope size={14} /> Send Email</>}
            </button>

            <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '0 0 16px', color: '#9ca3af', fontSize: 11 }}>
              <div style={{ flex: 1, height: 1, background: '#e5e7eb' }} />
              OR
              <div style={{ flex: 1, height: 1, background: '#e5e7eb' }} />
            </div>

            <button
              className="sec-modal-btn secondary"
              style={{ width: '100%' }}
              onClick={handleCopyQrCode}
            >
              {copyFeedback
                ? <><CheckCircle size={14} weight="fill" /> Copied!</>
                : <><Copy size={14} /> Copy QR Code to Clipboard</>}
            </button>

            <div className="sec-modal-actions" style={{ marginTop: 16 }}>
              <button className="sec-modal-btn secondary" onClick={closeShareModal}>
                <X size={14} /> Close
              </button>
            </div>
          </div>
        </div>
      )}

      <DialogModal
        isOpen={!!alertDialog}
        onClose={() => setAlertDialog(null)}
        title={alertDialog?.title}
        message={alertDialog?.message}
        type={alertDialog?.type || 'error'}
        primaryAction={{ label: 'OK', onClick: () => {} }}
      />
    </div>
    </Layout>
  );
}
